<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import IntegrationItem from './IntegrationItem.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const uiFlags = getters['integrations/getUIFlags'];
const searchQuery = ref('');

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const filteredIntegrations = computed(() => {
  if (!searchQuery.value) return integrationList.value;
  const query = searchQuery.value.toLowerCase();
  return integrationList.value.filter(
    item =>
      item.name.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
  );
});

const enabledCount = computed(
  () => integrationList.value.filter(item => item.enabled).length
);

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <div class="integrations-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 56px"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ $t('INTEGRATION_SETTINGS.HEADER') }}
      </h1>
      <div class="flex items-center gap-2">
        <span
          class="text-xs px-3 py-1 rounded-full"
          style="
            background: #ecfdf5;
            color: #065f46;
            border: 1px solid #a7f3d0;
            font-weight: 600;
          "
          >{{ enabledCount }} enabled</span>
      </div>
    </header>

    <!-- Description -->
    <div class="ws-desc px-6 py-3">
      <p class="text-sm" style="color: #4b5563; margin: 0">
        {{ replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION')) }}
      </p>
    </div>

    <!-- Search -->
    <div class="ws-toolbar px-6 py-3">
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-sm">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
            style="color: #9ca3af"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="ws-search reset-base w-full pl-10 pr-4 py-2 text-sm rounded-lg"
            placeholder="Search integrations..."
          />
        </div>
        <span class="text-sm" style="color: #6b7280">{{ filteredIntegrations.length }} integrations</span>
      </div>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <div class="ws-spinner" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredIntegrations.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <div class="ws-empty-icon">
          <span class="i-ph-puzzle-piece-bold size-10" style="color: #9ca3af" />
        </div>
        <h3 class="text-lg font-semibold mb-1" style="color: #111827">
          No integrations found
        </h3>
        <p class="text-sm" style="color: #6b7280">
          Try adjusting your search query
        </p>
      </div>

      <!-- Integrations Grid -->
      <div v-else class="ws-grid-wrap">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <IntegrationItem
            v-for="item in filteredIntegrations"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.integrations-page,
.dark .integrations-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-content {
  background: #ffffff;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.ws-grid-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  padding: 16px;
}
.ws-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: ws-spin 0.9s linear infinite;
}
@keyframes ws-spin {
  to {
    transform: rotate(360deg);
  }
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
</style>
